
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {Line2} from "../../../libs/three.js/lines/Line2.js";
import {LineGeometry} from "../../../libs/three.js/lines/LineGeometry.js";
import {LineMaterial} from "../../../libs/three.js/lines/LineMaterial.js";
import {DigitizationTools} from "../ToolRegistry.js";
import {FeatureVisual} from "../FeatureVisual.js";
import {createFeature} from "../Feature.js";
import {createStyledLabel} from "../DigitizationStyle.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "../ScreenSpaceScale.js";
import {computeSignedOffsetToBaseline} from "../geometry/perpendicularConstraint.js";
import {offsetPolyline} from "../geometry/polylineOffset.js";
import {createDashedLine, createMarker, setLinePositions} from "./perpendicularCapture.js";
import {promptDistance} from "../DistancePrompt.js";
import {settings} from "../settings/DigitizationSettings.js";

import {t, T} from "../i18n/index.js";
// Third tool in the "Perpendicular / Offset geometry" family (see
// perpendicularCapture.js's own doc comment for Vinkelret punkt/linje):
// digitizes a baseline, then lets the user pick a side + constant distance
// with the cursor, and registers a true constant-XY-offset of the *whole*
// baseline as a single LineString - see geometry/polylineOffset.js for the
// actual offset-with-miter-join construction.
//
// Deliberately its own capture flow, not folded into
// perpendicularCapture.js's runPerpendicularCapture(): that one commits
// individual points one at a time (a different distance per point is the
// whole point of Vinkelret linje); this one commits the *entire* offset
// result in a single click at one shared distance, which needs different
// preview (a live recomputed whole-polyline line, not per-point markers)
// and different completion semantics. What IS shared: baseline capture
// (DigitizationModule._captureVector(), unchanged), the side/distance core
// (computeSignedOffsetToBaseline(), also used by computePerpendicularConstraint()),
// and the small marker/dashed-line/label preview helpers.

const PREVIEW_COLOR = 0xffdc00; // matches the existing "computed/preview point" convention

function createSolidPolyline(color){
	const geometry = new LineGeometry();
	geometry.setPositions([0, 0, 0, 0, 0, 0]);
	const material = new LineMaterial({color, linewidth: 2, resolution: new THREE.Vector2(1000, 1000)});
	material.depthTest = false;
	const line = new Line2(geometry, material);
	line.computeLineDistances();
	line.visible = false;
	return line;
}

// Line2/LineGeometry.setPositions() takes a flat [x,y,z,x,y,z,...] array of
// any length each call - unlike setLinePositions() (perpendicularCapture.js),
// which only ever handles a fixed 2-point segment, this repositions a
// polyline of however many points the live offset result currently has.
function setPolylinePositions(line, points){
	if(points.length < 2){ line.visible = false; return; }

	const origin = points[0];
	line.position.copy(origin);

	const flat = [];
	for(const p of points) flat.push(p.x - origin.x, p.y - origin.y, p.z - origin.z);

	line.geometry.setPositions(flat);
	line.geometry.verticesNeedUpdate = true;
	line.geometry.computeBoundingSphere();
	line.computeLineDistances();
	line.visible = true;
}

export const ParallelOffsetTool = {
	id: "digitize_parallel_offset",
	name: T("Parallelforskydning"),
	category: "specialized",
	kind: "registration",
	toolbarGroup: "registration",
	toolbarOrder: 100,
	icon: "parallel_offset.svg",
	resultGeometryType: "LineString",
	featureType: T("Parallelforskydning"),

	tooltip: T("Digitalisér en basislinje og vælg derefter side og afstand. Der oprettes en parallel linje med konstant afstand til basislinjen."),

	instructions: {
		baseline: T("Basislinje: Venstreklik for at placere punkter langs basislinjen. Højreklik for at afslutte basislinjen."),
		offset: T("Flyt musen for at vælge side og afstand - en komplet parallel linje vises som preview. Venstreklik for at gemme."),
	},

	// Shown FIRST, before any of the registration workflow starts (including
	// baseline capture) - see the shared "Vinkelret / parallel" distance
	// prompt requirement (DistancePrompt.js). Cancelling it aborts the tool
	// activation entirely, same as tools/perpendicularCapture.js's own prompt.
	customCapture(dm, tool){
		const defaultValue = settings.get("perpendicularParallel.defaultDistance");

		promptDistance(dm, {
			title: tool.name,
			description: t("Angiv en fast afstand til basislinjen, eller lad feltet stå tomt for at vælge afstanden dynamisk med musen under registrering."),
			defaultValue,
		}).then((fixedDistance) => {
			if(fixedDistance === undefined){
				if(dm.activeTool === tool) dm.stopInsertion();
				return;
			}

			dm._showInstruction(tool.name, tool.instructions.baseline);

			dm._captureVector((baseline) => {
				if(!baseline) return; // cancelled, or fewer than 2 points captured

				runOffsetPhase(dm, tool, baseline, fixedDistance);
			});
		});
	},
	// Restore extension point (see DigitizationModule._buildLoadedFeature): the per-vertex role names the capture flow sets are part of
	// the finished line's vertex labels; the baseline and offset construction are never recreated.
	decorateLoaded(visual){
		visual.vertexRoleNames = visual.points.map((p, i) => `${ParallelOffsetTool.name} ${i + 1}`);
		for(let i = 0; i < visual.points.length; i++) visual._refreshVertexLabel(i);
	},
};

DigitizationTools.register(ParallelOffsetTool);

function runOffsetPhase(dm, tool, baseline, fixedDistance){
	baseline.visible = settings.get("perpendicularParallel.showBaselineDuringCapture");
	const viewer = dm.viewer;
	const domElement = viewer.renderer.domElement;

	dm._showInstruction(tool.name, tool.instructions.offset);

	const qMarker = createMarker(PREVIEW_COLOR);
	const perpHelperLine = createDashedLine(PREVIEW_COLOR);
	const distanceLabel = createStyledLabel("");
	const offsetPreviewLine = createSolidPolyline(PREVIEW_COLOR);
	dm.scene.add(qMarker, perpHelperLine, distanceLabel, offsetPreviewLine);

	let currentOffsetPoints = null;
	let finished = false;

	const updateScales = () => {
		const camera = viewer.scene.getActiveCamera();
		const size = viewer.renderer.getSize(new THREE.Vector2());
		const w = size.width, h = size.height;
		const cameraPos = camera.position;

		if(qMarker.visible){
			const s = computeScreenSpaceScale(camera, qMarker.getWorldPosition(new THREE.Vector3()), cameraPos, w, h, ScreenSpaceSizes.computedPoint);
			qMarker.scale.set(s, s, s);
		}
		if(distanceLabel.visible){
			const s = computeScreenSpaceScale(camera, distanceLabel.getWorldPosition(new THREE.Vector3()), cameraPos, w, h, ScreenSpaceSizes.label);
			distanceLabel.scale.set(s, s, s);
		}
		perpHelperLine.material.resolution.set(w, h);
		offsetPreviewLine.material.resolution.set(w, h);
	};

	const onUpdate = () => updateScales();
	viewer.addEventListener("update", onUpdate);

	const hidePreview = () => {
		qMarker.visible = false;
		perpHelperLine.visible = false;
		distanceLabel.visible = false;
		offsetPreviewLine.visible = false;
		currentOffsetPoints = null;
	};

	const updatePreview = (picked) => {
		const baselinePoints = baseline.points.map((p) => p.position);
		const offsetInfo = computeSignedOffsetToBaseline(picked, baselinePoints);
		if(!offsetInfo){ hidePreview(); return; }

		// Locks the magnitude to `fixedDistance` while keeping whichever side
		// the cursor is on - capture-flow-only, doesn't touch
		// computeSignedOffsetToBaseline()/offsetPolyline() themselves. Falls
		// back to the positive side if the cursor sits exactly on the
		// baseline (signedDistance 0 has no side to preserve).
		const signedDistance = fixedDistance !== null
			? fixedDistance * Math.sign(offsetInfo.signedDistance || 1)
			: offsetInfo.signedDistance;

		const offsetResult = offsetPolyline(baselinePoints, signedDistance);
		if(offsetResult.status !== "ok"){ hidePreview(); return; }

		currentOffsetPoints = offsetResult.points;

		setPolylinePositions(offsetPreviewLine, offsetResult.points);

		qMarker.visible = settings.get("perpendicularParallel.showProjectionPoint");
		if(qMarker.visible) qMarker.position.copy(offsetInfo.q);

		// helper line from Q (on the baseline) to the picked side/distance,
		// at the picked point's own Z - same visual language as Vinkelret
		// punkt/linje's own perpendicular helper line, reusing the exact same
		// side/distance the whole-polyline offset above was computed from
		const helperEnd = new THREE.Vector3(
			offsetInfo.q.x + offsetInfo.perp.x * signedDistance,
			offsetInfo.q.y + offsetInfo.perp.y * signedDistance,
			picked.z,
		);
		perpHelperLine.visible = settings.get("perpendicularParallel.showHelperLines");
		if(perpHelperLine.visible) setLinePositions(perpHelperLine, offsetInfo.q, helperEnd);

		distanceLabel.visible = settings.get("perpendicularParallel.showDistance");
		if(distanceLabel.visible){
			distanceLabel.setText(t("Afstand: {0} m", Math.abs(signedDistance).toFixed(3)));
			distanceLabel.position.copy(helperEnd);
		}
	};

	const onMouseMove = (e) => {
		if(finished || dm.isPaused()) return;
		const picked = dm._pickPlacementPosition(e);
		if(!picked){ hidePreview(); return; }
		updatePreview(picked);
	};

	const removeConstructionGeometry = () => {
		dm.scene.remove(baseline);
		dm.scene.remove(qMarker);
		dm.scene.remove(perpHelperLine);
		dm.scene.remove(distanceLabel);
		dm.scene.remove(offsetPreviewLine);
	};

	const cleanup = () => {
		domElement.removeEventListener('mousemove', onMouseMove, false);
		domElement.removeEventListener('mouseup', onMouseUp, false);
		viewer.removeEventListener('update', onUpdate);
		viewer.removeEventListener('cancel_insertions', onCancel);
	};

	const finalizeResult = (points) => {
		const visual = new FeatureVisual("LineString");
		for(const p of points) visual.appendAtEnd(p.clone());

		const feature = createFeature({
			geometryType: "LineString",
			coordinates: visual.toCoordinates(),
			category: tool.category,
			tool: tool.id,
			featureType: tool.featureType || tool.name,
			name: `${tool.name} ${dm.featureManager.features.length + 1}`,
		});

		visual.vertexRoleNames = points.map((p, i) => `${tool.name} ${i + 1}`);
		visual.setInfo(feature.properties.name, feature.properties.description);
		dm.scene.add(visual);

		dm._wireFeatureLifecycle(feature, visual);

		dm.dispatchEvent({type: "feature_created", feature, visual});
	};

	const onCancel = (e) => {
		if(finished) return;
		finished = true;
		cleanup();
		removeConstructionGeometry();
		// ESC or otherwise: there is nothing partially committed to preserve
		// here (a single left-click both computes and commits the whole
		// result in one step - see onMouseUp), so every cancellation path is
		// a full, unconditional discard
	};

	const onMouseUp = (e) => {
		if(finished || dm.isPaused() || dm._consumeCopyClickGuard()) return;

		if(e.button === THREE.MOUSE.LEFT){
			if(!currentOffsetPoints) return;

			finished = true;
			cleanup();
			removeConstructionGeometry();
			finalizeResult(currentOffsetPoints);

			// single-shot, like the other tools in this family: the user must
			// reselect the tool to digitize another baseline+offset
			if(dm.activeTool === tool) dm.stopInsertion();
		}
		// right-click has no defined action in this phase - only the
		// baseline phase's right-click has meaning (ends the baseline);
		// ESC remains the only cancel path here
	};

	domElement.addEventListener('mousemove', onMouseMove, false);
	domElement.addEventListener('mouseup', onMouseUp, false);
	viewer.addEventListener('cancel_insertions', onCancel);
}
